'use client';

import React, { useEffect, useState } from 'react';

import clsx from 'clsx';
import { useSearchParams } from 'next/navigation';

import { AddToBagButton } from '../AddToBagButton';
import { BuyNowButton } from '../BuyNowButton';
import { FreeShipping } from '../FreeShippping';
import { MessageSellerButton } from '../MessageSellerButton';
import { SellerInactiveDisclaimer } from '../SellerInactiveDisclaimer';
import { SizeSelect } from '../SizeSelect';

import styles from './style.module.css';

import { MakeOfferButton } from '@/app/[locale]/products/[slug]/components/MakeOfferButton';
import { MobileProductActions } from '@/app/[locale]/products/[slug]/components/MobileProductActions';
import { MakeOfferModal } from '@/components/MakeOfferModal';
import { SellerVacationBanner } from '@/components/SellerVacationBanner';
import {
  ON_IDENTIFIER,
  VARIANT_IDENTIFIER,
} from '@/modules/experiments/config';
import { useExperiments } from '@/modules/experiments/useExperiments';
import { useListingCountry } from '@/modules/listing/hooks/useListingCountry';
import { getSmallestProductPictureOrVideoFormat } from '@/modules/pictures/helpers/getSmallestProductPictureOrVideoFormat';
import { getProductPriceFromPricing } from '@/modules/price/helpers/getProductPriceFromPricing';
import { ShippingDiscountType } from '@/modules/product/aliases';
import { isProductOnSale } from '@/modules/product/helpers/isProductOnSale';
import { useBuyNowAction } from '@/modules/product/hooks/useBuyNowAction';
import { useProductBySlugBasicPDP } from '@/modules/product/hooks/useProductBySlugBasicPDP';
import { useProductBySlugExtendedPDP } from '@/modules/product/hooks/useProductBySlugExtendedPDP';
import { useSellerFriction } from '@/modules/shippingConfidence/useSellerFriction';
import { useCurrentUser } from '@/modules/user/useCurrentUser';

type Props = {
  isCondensedPLAVariant?: boolean;
};

export function BuyerActions({ isCondensedPLAVariant }: Props) {
  const { data: productBasic, isLoading: productBasicLoading } =
    useProductBySlugBasicPDP();
  const { data: productExtended, isLoading: productExtendedLoading } =
    useProductBySlugExtendedPDP();

  const { handleBuyNowButtonClick } = useBuyNowAction();
  const searchParams = useSearchParams();
  const [isAuthenticated] = useCurrentUser();
  const { userCountry: buyerCountry } = useListingCountry();

  const {
    web_6304_show_suggested_offer_in_pdp,
    ppx_399_buyer_actions_vacation_mode,
  } = useExperiments([
    'web_6304_show_suggested_offer_in_pdp',
    'ppx_399_buyer_actions_vacation_mode',
  ]);

  const isShowSuggestedOfferVariant =
    web_6304_show_suggested_offer_in_pdp === VARIANT_IDENTIFIER;

  const [selectedVariantId, setSelectedVariantId] = useState<string>('');
  const [isMakeOfferModalOpen, setIsMakeOfferModalOpen] = useState(false);
  const [showOfferSentContent, setShowOfferSentContent] = useState(false);
  const [predefinedOfferValue, setPredefinedOfferValue] = useState('');

  const sizes = productExtended?.sizes || [];
  const hasMultipleSizes = Boolean(sizes.length > 1);
  const preventBuy = Boolean(hasMultipleSizes && !selectedVariantId);
  const isSellerOnVacation =
    ppx_399_buyer_actions_vacation_mode === ON_IDENTIFIER &&
    productExtended?.seller_activity?.is_on_vacation;

  function handleSizeChange(e: React.ChangeEvent<HTMLSelectElement>) {
    setSelectedVariantId(e.target.value);
  }

  function toggleMakeOfferModalOpen(isOpen: boolean) {
    if (!isOpen && predefinedOfferValue !== '') {
      setPredefinedOfferValue('');
    }
    setIsMakeOfferModalOpen(isOpen);
  }

  function onOfferSent() {
    setShowOfferSentContent(true);
  }

  function handleMakeOfferClick(newOfferValue?: string) {
    if (isShowSuggestedOfferVariant) {
      setPredefinedOfferValue(newOfferValue || '');
    }
    setIsMakeOfferModalOpen((val) => !val);
  }

  useEffect(() => {
    if (productExtended && productExtended.sizes && !hasMultipleSizes) {
      setSelectedVariantId(String(productExtended.sizes[0].id));
    }
  }, [productExtended?.sizes, hasMultipleSizes]);

  useEffect(() => {
    const makeOfferModal = searchParams.get('makeOfferModal');
    if (makeOfferModal === 'true' && productExtended) {
      setIsMakeOfferModalOpen(true);
      const url = new URL(window.location.href);
      url.searchParams.delete('makeOfferModal');
      window.history.replaceState({}, '', url.toString());
    }
  }, [searchParams, productExtended]);

  const buyerCountryMatchesProductCountry =
    productBasic?.country?.toUpperCase() === buyerCountry.toUpperCase();
  const hasFreeSellerSubsidisedShipping =
    buyerCountryMatchesProductCountry &&
    productBasic?.shipping_discounts?.national_shipping?.discount_type ===
      ShippingDiscountType.FREE_SHIPPING;
  const showSellerFriction =
    useSellerFriction() &&
    !isCondensedPLAVariant &&
    isProductOnSale(productBasic?.status);

  const noSellerFrictionCTAs = (
    <>
      <BuyNowButton
        onBuyButtonClick={() => handleBuyNowButtonClick(selectedVariantId)}
        preventBuy={preventBuy}
        status={productBasic?.status}
        isLoading={productBasicLoading || productExtendedLoading}
      />
      <div className={styles.secondaryCtaGroup}>
        <MakeOfferButton
          className={clsx(styles.secondaryCta, styles.desktopOnly)}
          preventBuy={preventBuy}
          offerSent={showOfferSentContent}
          onClickAction={handleMakeOfferClick}
          showForLoggedOutUsers
          position="top"
        />
        <AddToBagButton
          className={clsx(styles.secondaryCta, styles.desktopOnly)}
          preventBuy={preventBuy}
          hasMultipleSizes={hasMultipleSizes}
          selectedVariantId={
            selectedVariantId ? Number(selectedVariantId) : undefined
          }
        />
      </div>
    </>
  );
  const sellerFrictionCTAs = (
    <>
      {/* mWeb shows Message seller only in the bottom bar, matching iOS */}
      <MessageSellerButton
        className={clsx(styles.messageSeller, styles.desktopOnly)}
      />
      <MakeOfferButton
        className={clsx(styles.secondaryCta, styles.desktopOnly)}
        preventBuy={preventBuy}
        offerSent={showOfferSentContent}
        onClickAction={handleMakeOfferClick}
        showForLoggedOutUsers
        position="top"
      />
      <SellerInactiveDisclaimer className={styles.sellerInactiveDisclaimer} />
    </>
  );

  function renderCTAs() {
    return showSellerFriction ? sellerFrictionCTAs : noSellerFrictionCTAs;
  }
  return (
    <>
      <div className={styles.container}>
        {hasMultipleSizes && (
          <SizeSelect
            sizes={sizes}
            onChange={handleSizeChange}
            value={selectedVariantId}
          />
        )}
        {isSellerOnVacation ? (
          <SellerVacationBanner
            username={productBasic?.seller.username || ''}
            sellerId={String(productBasic?.seller.id || '')}
            productIds={[String(productBasic?.id || '')]}
          />
        ) : (
          renderCTAs()
        )}

        {!hasFreeSellerSubsidisedShipping && !isCondensedPLAVariant && (
          <FreeShipping className={styles.desktopOnly} />
        )}
      </div>
      <MobileProductActions
        preventBuy={preventBuy}
        hasMultipleSizes={hasMultipleSizes}
        selectedVariantId={
          selectedVariantId ? Number(selectedVariantId) : undefined
        }
        onMakeOfferClick={handleMakeOfferClick}
        showOfferSentContent={showOfferSentContent}
        showSellerFriction={showSellerFriction}
      />
      {productBasic && productExtended && (
        <MakeOfferModal
          isAuthenticated={isAuthenticated}
          isOpen={isMakeOfferModalOpen}
          setOpen={toggleMakeOfferModalOpen}
          productId={productBasic.id}
          showOfferSentContent={showOfferSentContent}
          onOfferSent={onOfferSent}
          productImageHref={
            productBasic
              ? getSmallestProductPictureOrVideoFormat(productBasic)?.url
              : ''
          }
          sizeName={
            selectedVariantId
              ? productExtended.sizes?.find(
                  (size) => size.id === Number(selectedVariantId)
                )?.name
              : productExtended?.sizes?.[0].name
          }
          productPrice={{
            ...getProductPriceFromPricing(productExtended.pricing),
            displayPrice: productExtended.pricing.display_price,
          }}
          selectedVariantId={
            selectedVariantId ? Number(selectedVariantId) : undefined
          }
          predefinedOfferPrice={predefinedOfferValue}
        />
      )}
    </>
  );
}
